import React, { useEffect, useState } from 'react';
import { alpacaService, DeviceToken } from '../../services/AlpacaService';
import styles from './Settings.module.css';

function formatLastSeen(iso: string): string {
  if (!iso) return '';
  const then = new Date(iso);
  if (isNaN(then.getTime())) return iso;
  const diffMs = Date.now() - then.getTime();
  const mins = Math.round(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} hr ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;
  return then.toLocaleDateString();
}

function displayName(d: DeviceToken): string {
  if (d.deviceModel) return d.deviceModel;
  if (d.platform === 'ios') return 'iOS device';
  if (d.platform === 'android') return 'Android device';
  return 'Unknown device';
}

const ConnectedDevices: React.FC = () => {
  const [devices, setDevices] = useState<DeviceToken[] | null>(null);
  const [error, setError] = useState<string>('');
  const [revokingId, setRevokingId] = useState<string>('');

  useEffect(() => {
    alpacaService
      .listDevices()
      .then((r) => setDevices(r.devices))
      .catch((e: Error) => setError(e.message || 'Failed to load devices'));
  }, []);

  const handleRevoke = async (deviceId: string) => {
    if (!window.confirm('Disconnect this device? It will stop receiving push notifications.')) {
      return;
    }
    setError('');
    setRevokingId(deviceId);
    try {
      await alpacaService.revokeDevice(deviceId);
      setDevices((prev) => (prev ? prev.filter((d) => d.deviceId !== deviceId) : prev));
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to disconnect device';
      setError(msg);
    } finally {
      setRevokingId('');
    }
  };

  return (
    <section className={styles.section}>
      <h2 className={styles.sectionTitle}>Connected Devices</h2>
      <div className={styles.card}>
        <p className={styles.mfaDesc}>
          Mobile devices registered to receive push notifications. Disconnect a device to stop sending notifications to
          it.
        </p>

        {error && <p className={styles.error}>{error}</p>}

        {devices === null && <p className={styles.muted}>Loading devices...</p>}

        {devices !== null && devices.length === 0 && (
          <p className={styles.muted}>
            No devices connected. Install the Signaldeck mobile app and sign in to register for push notifications.
          </p>
        )}

        {devices && devices.length > 0 && (
          <div>
            {devices.map((d) => (
              <div key={d.deviceId} className={styles.row}>
                <div>
                  <div className={styles.value}>{displayName(d)}</div>
                  <div className={styles.label}>
                    {d.platform || 'unknown'} · last seen {formatLastSeen(d.lastSeenAt)}
                  </div>
                </div>
                <button
                  className={styles.btnDanger}
                  onClick={() => handleRevoke(d.deviceId)}
                  disabled={revokingId === d.deviceId}
                >
                  {revokingId === d.deviceId ? 'Disconnecting...' : 'Disconnect'}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default ConnectedDevices;
